import React from 'react';
import type { ProductCard, ProductAction, RichContent } from '../../types';
import { isValidImageUrl } from '../../utils/fileValidation';

interface ProductCardsMessageProps {
  content: RichContent;
  onAction?: (payload: { action: ProductAction; product: ProductCard }) => void;
}

const ProductCardsMessage = React.memo(function ProductCardsMessage({
  content,
  onAction,
}: ProductCardsMessageProps) {
  const products = Array.isArray(content.products) ? content.products : [];
  if (products.length === 0) return null;

  const isStacked = content.layout === 'stack';
  const productsScrollClass = `rw-products-scroll${isStacked ? ' rw-products-scroll-stack' : ''}`;

  return (
    <div className="rw-products" role="region" aria-label="Product recommendations">
      {content.headline && <div className="rw-products-headline">{content.headline}</div>}
      {content.subhead && <div className="rw-products-subhead">{content.subhead}</div>}
      <div className={productsScrollClass}>
        {products.map((product, idx) => (
          <div key={`${product.id}-${idx}`} className="rw-product-card">
            {product.image && isValidImageUrl(product.image) && (
              <div className="rw-product-media">
                <img src={product.image} alt={product.title} loading="lazy" decoding="async" />
              </div>
            )}
            <div className="rw-product-body">
              <div className="rw-product-title">{product.title}</div>
              {product.price && <div className="rw-product-price">{product.price}</div>}
              {product.description && <div className="rw-product-desc">{product.description}</div>}
              {product.badges && product.badges.length > 0 && (
                <div className="rw-product-badges">
                  {product.badges.map(badge => {
                    const lower = badge.toLowerCase();
                    const variant = lower === 'sale'
                      ? ' rw-product-badge-sale'
                      : lower === 'out of stock'
                        ? ' rw-product-badge-oos'
                        : '';
                    return (
                      <span key={`${product.id}-${badge}`} className={`rw-product-badge${variant}`}>
                        {badge}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            {product.actions && product.actions.length > 0 && (
              <div className="rw-product-actions">
                {product.actions.map((action, idx) => (
                  <button
                    key={`${product.id}-action-${idx}`}
                    type="button"
                    className={`rw-product-action ${
                      action.type === 'add_to_cart' || action.type === 'checkout'
                        ? 'rw-product-action-primary'
                        : ''
                    }`}
                    aria-label={`${action.label} - ${product.title}`}
                    onClick={() => onAction?.({ action, product })}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

export default ProductCardsMessage;
